import { axios } from "../helpers/axios";
import { setNoAlertStatus } from "../helpers/storage";

/**
 * Fetches all available credit plans.
 * Suppresses global alerts during the request.
 * @returns Promise resolving to the list of plans.
 */
export const getAllPlans = (): Promise<any> => {
  setNoAlertStatus(true);
  return axios.get(`plans/get_all_plans/`);
};

/**
 * Assigns a subscription plan to a user.
 * Suppresses global alerts during the request.
 * @param payload - Subscription assignment data.
 * @returns Promise resolving to the assignment result.
 */
export const assignSubscription = (payload: any): Promise<any> => {
  setNoAlertStatus(true);
  return axios.post(`plans/assign_subscription/`, payload);
};

/**
 * Fetches the current user's active plan.
 * Suppresses global alerts during the request.
 * @returns Promise resolving to the user's plan details.
 */
export const getUserPlan = (): Promise<any> => {
  setNoAlertStatus(true);
  return axios.get(`plans/get_user_plan/`, {
    suppressAlerts: { statusCode: [403] },
  } as any);
};
/**
 * Purchases add-on plans for the user.
 * Suppresses global alerts during the request.
 * @param payload - Add-on purchase data.
 * @returns Promise resolving to the purchase result.
 */
export const getAddOnPlans = (payload: any): Promise<any> => {
  setNoAlertStatus(true);
  return axios.post(`addons/buy_add_on/`, payload);
};

/**
 * Fetches the user's transaction history.
 * Suppresses global alerts during the request.
 * @param params - Pagination and sorting parameters.
 * @returns Promise resolving to the transaction history.
 */
export const getTransactionHistory = (params: {
  current: number;
  limit_to: number;
  sort: number;
  domain?: string;
  from_date?: string;
  to_date?: string;
}): Promise<any> => {
  setNoAlertStatus(true);
  let url = `plans/get_usage_history?current=${params.current}&limit_to=${params.limit_to}&sort=${params.sort}`;
  if (params.domain) url += `&domain=${params.domain}`;
  if (params.from_date) url += `&from_date=${params.from_date}`;
  if (params.to_date) url += `&to_date=${params.to_date}`;
  return axios.get(url);
};

/**
 * Fetches the user's purchase history.
 * Suppresses global alerts during the request.
 * @param params - Pagination and sorting parameters.
 * @returns Promise resolving to the purchase history.
 */
export const getPurchaseHistory = (params: {
  current: number;
  limit_to: number;
  sort: number;
  domain?: string;
  from_date?: string;
  to_date?: string;
}): Promise<any> => {
  setNoAlertStatus(true);
  let url = `payments/payment_history?current=${params.current}&limit_to=${params.limit_to}&sort=-1`;
  if (params.domain) url += `&domain=${params.domain}`;
  if (params.from_date) url += `&from_date=${params.from_date}`;
  if (params.to_date) url += `&to_date=${params.to_date}`;
  return axios.get(url);
};

export const exportUsageHistory = (payload: any) => {
  return axios.post(`plans/download_usage_history/`, payload);
};

export const exportTransactionHistory = (payload: any) => {
  return axios.post(`plans/download_purchase_history/`, payload);
};

/**
 * API's for the
 * Payment Gateway for the addons
 */

export const initiatePayment = (payload: any): Promise<any> => {
  return axios.post(`/livis/v2/payments/initiate_payment/`, payload);
};

// Add to your services file
export const verifyPayment = (sessionId: string): Promise<any> => {
  return axios.get(
    `/livis/v2/payments/verify_payment/?session_id=${sessionId}`
  );
};

/**
 * Fetches the list of available addons.
 * @returns Promise resolving to the list of addons.
 */
export const listAddons = (): Promise<any> => {
  setNoAlertStatus(true)
  return axios.get(`addons/list_addons/`)
}

export const submitAddonRequest = (payload: any): Promise<any> => {
  return axios.post(`addons/request_addon/`, payload)
}

export const getAddonRequests = (params: {
  domain?: string;
  current?: number;
  limit_to?: number;
  sort?: number;
}): Promise<any> => {
  setNoAlertStatus(true)
  const queryParams = new URLSearchParams()
  if (params.domain) queryParams.set("domain", params.domain)
  if (params.current !== undefined) queryParams.set("current", String(params.current))
  if (params.limit_to !== undefined) queryParams.set("limit_to", String(params.limit_to))
  if (params.sort !== undefined) queryParams.set("sort", String(params.sort))
  const qs = queryParams.toString()
  const url = `addons/get_all_addon_requests/${qs ? `?${qs}` : ""}`
  return axios.get(url)
}

export const getMyAddonRequests = (params: {
  current?: number;
  limit_to?: number;
  sort?: number;
  state?: number;
}): Promise<any> => {
  setNoAlertStatus(true)
  let url = `addons/get_my_addon_requests/?`
  if (params.current !== undefined) url += `current=${params.current}&`
  if (params.limit_to !== undefined) url += `limit_to=${params.limit_to}&`
  if (params.sort !== undefined) url += `sort=${params.sort}&`
  if (params.state !== undefined) url += `state=${params.state}&`
  if (url.endsWith('&') || url.endsWith('?')) {
    url = url.slice(0, -1)
  }
  return axios.get(url)
}


export const approveAddonRequest = (payload: {
  addon_request_id: string;
  domain: string;
}): Promise<any> => {
  return axios.post(`addons/approve_addon_request/`, payload)
}

export const denyAddonRequest = (payload: {
  addon_request_id: string;
  domain: string;
}): Promise<any> => {
  return axios.post(`addons/deny_addon_request/`, payload)
}

/**
 * Superuser "Plan Management" tab — everything about one domain's assigned
 * plan: plan details, its add-on requests (with status), and its invoices.
 *
 * MOCKED for now — the real endpoint (`GET credits/get_assigned_plan/?domain=`)
 * doesn't exist on the backend yet. This returns a hardcoded dummy payload
 * shaped like the real response is expected to look, so the UI/hook/query
 * plumbing can be built and swapped for a live call later by uncommenting
 * the axios line below and deleting the mock block.
 */
export const getAssignedPlan = (domain: string): Promise<any> => {
  setNoAlertStatus(true);
  // return axios.get(`credits/get_assigned_plan/?domain=${encodeURIComponent(domain)}`);

  const dummyResponse = {
    domain,
    plan: {
      plan_name: "enterprise",
      status: "active", // active | expired | trial
      purchased_on: "2025-08-12T00:00:00Z",
      expires_on: "2026-08-11T00:00:00Z",
      price: 24000,
      currency: "USD",
      billing_cycle: "annual",
    },
    addon_summary: {
      requested: 6,
      approved: 3,
      pending: 1,
      rejected: 2,
      purchased: 3,
    },
    addons: [
      { _id: "addon-1", addon_type: "ai_inspectors", count: 2, status: "approved", requested_by: `admin@${domain}`, requested_on: "2026-06-02T09:15:00Z", resolved_on: "2026-06-03T11:00:00Z", total_price: 300 },
      { _id: "addon-2", addon_type: "gpu_hours", count: 50, status: "approved", requested_by: `admin@${domain}`, requested_on: "2026-05-20T14:30:00Z", resolved_on: "2026-05-21T08:45:00Z", total_price: 250 },
      { _id: "addon-3", addon_type: "training_assets", count: 20, status: "approved", requested_by: `ops@${domain}`, requested_on: "2026-04-11T10:00:00Z", resolved_on: "2026-04-12T09:30:00Z", total_price: 40 },
      { _id: "addon-4", addon_type: "report_assets", count: 10, status: "pending", requested_by: `ops@${domain}`, requested_on: "2026-07-25T16:20:00Z", resolved_on: null, total_price: 150 },
      { _id: "addon-5", addon_type: "user", count: 5, status: "denied", requested_by: `admin@${domain}`, requested_on: "2026-03-04T12:00:00Z", resolved_on: "2026-03-05T09:00:00Z", total_price: 50 },
      { _id: "addon-6", addon_type: "evaluate", count: 1, status: "denied", requested_by: `admin@${domain}`, requested_on: "2026-02-18T08:00:00Z", resolved_on: "2026-02-19T10:15:00Z", total_price: 20 },
    ],
    invoices: [
      { _id: "inv-1", invoice_number: "INV-2026-0142", description: "Enterprise Plan — Annual Renewal", amount: 24000, currency: "USD", status: "success", created_at: "2025-08-12T09:00:00Z" },
      { _id: "inv-2", invoice_number: "INV-2026-0098", description: "AI Inspector x2, GPU Hours x50", amount: 550, currency: "USD", status: "success", created_at: "2026-06-03T11:05:00Z" },
      { _id: "inv-3", invoice_number: "INV-2026-0061", description: "Training Assets x20", amount: 40, currency: "USD", status: "success", created_at: "2026-04-12T09:35:00Z" },
      { _id: "inv-4", invoice_number: "INV-2026-0142-R", description: "Report Assets x10 (awaiting approval)", amount: 150, currency: "USD", status: "pending", created_at: "2026-07-25T16:25:00Z" },
    ],
  };

  return new Promise((resolve) => {
    setTimeout(() => resolve({ status: 200, data: { data: dummyResponse } }), 400);
  });
};

